import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { MarketStatus } from '@sportspredict/shared';
import { TABS } from '@/constants';
import {
	setSeenOpenMarketIds,
	setShouldAnimate,
	useSeenOpenMarketIdsSelector,
	useShouldAnimateSelector,
} from '@/store/slices';

interface UseLobbyOpenMarketsProps {
	markets: any[];
	userTrades: any[];
	slatesData: any[];
	lobbyId: string | undefined;
	activeTab: string;
	hasSeenInfo: boolean | undefined;
	slateTradeLimit: number | undefined;
	slateUserTradeCounts: Record<string, any> | undefined;
}

export function useLobbyOpenMarkets({
	markets,
	userTrades,
	slatesData,
	lobbyId,
	activeTab,
	hasSeenInfo,
	slateTradeLimit,
	slateUserTradeCounts,
}: UseLobbyOpenMarketsProps) {
	const dispatch: any = useDispatch();

	const openMarketIds = useMemo(
		() =>
			markets
				.filter(
					(m: any) =>
						m.status === MarketStatus.Open && !userTrades?.some((t: any) => t.market_id === m.id)
				)
				.map((m: any) => m.id),
		[markets, userTrades]
	);

	const avlToTradeMarketCount = openMarketIds?.length;

	const prevCountRef = useRef(avlToTradeMarketCount);

	const isShouldAnimate = useShouldAnimateSelector(lobbyId);

	useEffect(() => {
		if (prevCountRef.current === 1 && avlToTradeMarketCount === 0) {
			dispatch((setShouldAnimate as any)({ lobbyId, value: true }));
		}

		if (avlToTradeMarketCount > 1 && isShouldAnimate) {
			dispatch((setShouldAnimate as any)({ lobbyId, value: false }));
		}

		prevCountRef.current = avlToTradeMarketCount;
	}, [dispatch, lobbyId, isShouldAnimate, avlToTradeMarketCount]);

	const seenOpenMarketIds = useSeenOpenMarketIdsSelector(lobbyId);

	useEffect(() => {
		if (activeTab === TABS.MARKETS && openMarketIds?.length > 0) {
			const newMarketIds = openMarketIds.filter((id: any) => !seenOpenMarketIds.includes(id));

			if (newMarketIds.length > 0) {
				dispatch(
					(setSeenOpenMarketIds as any)({
						lobbyId,
						marketIds: [...seenOpenMarketIds, ...newMarketIds],
					})
				);
			}
		}
	}, [activeTab, openMarketIds, seenOpenMarketIds, lobbyId, dispatch]);

	useEffect(() => {
		if (lobbyId && openMarketIds?.length && !hasSeenInfo) {
			dispatch((setSeenOpenMarketIds as any)({ lobbyId, marketIds: openMarketIds }));
		}
	}, [lobbyId, hasSeenInfo, dispatch, openMarketIds]);

	const slateAvailableMarkets = useMemo(() => {
		const result: Record<string, { name: any; count: number; markets: any[] }> = {};

		if (!markets?.length || !slatesData?.length) return result;

		markets.forEach((market: any) => {
			if (market.status !== MarketStatus.Open || !market.match_id) return;

			const hasUserTrade = userTrades?.some((t: any) => t.market_id === market.id);
			if (hasUserTrade) return;

			const slate = slatesData.find((sl: any) =>
				sl.matches?.some((m: any) => m.id === market.match_id)
			);

			if (!slate) return;

			if (!result[slate.id]) {
				result[slate.id] = {
					name: slate.name,
					count: 1,
					markets: [market.id],
				};
			} else {
				result[slate.id].count++;
				result[slate.id].markets.push(market.id);
			}
		});

		return result;
	}, [markets, slatesData, userTrades]);

	const availableMarkets = useMemo(() => {
		const available: any[] = [];
		if (!slateTradeLimit) return [];

		Object.entries(slateAvailableMarkets).forEach(([id, slData]: [string, any]) => {
			const made = slateUserTradeCounts?.[String(id)]?.total ?? 0;

			if (made < slateTradeLimit) {
				available.push(...slData.markets);
			}
		});

		return available;
	}, [slateAvailableMarkets, slateTradeLimit, slateUserTradeCounts]);

	const unseenOpenMarketCount = useMemo(() => {
		if (!openMarketIds?.length || !availableMarkets?.length) return 0;
		if (!seenOpenMarketIds?.length) return openMarketIds.length;
		return openMarketIds.filter(
			(id: any) => !seenOpenMarketIds.includes(id) && availableMarkets.includes(id)
		).length;
	}, [openMarketIds, seenOpenMarketIds, availableMarkets]);

	return {
		openMarketIds,
		avlToTradeMarketCount,
		isShouldAnimate,
		slateAvailableMarkets,
		availableMarkets,
		unseenOpenMarketCount,
	};
}
